import React, { useState } from 'react';
import { Autocomplete, AutocompleteInput, AutocompleteOptions } from '@flixbus/honeycomb-react';
import { useVoucherFormContext } from '../../Context';
import { Relation, AllowedRelationsRule, ForbiddenRelationsRule } from '../../../../types/Rules';
import {
  CountriesOptions,
  AutocompleteCountry,
  CountriesAutocomplete,
} from '../AllowedandForbiddenCountries/Countries';

interface RelationPairInputsProps {
  relationType: Relation;
  relationId: string;
}

const RelationPairInputs: React.FC<RelationPairInputsProps> = ({ relationType, relationId }) => {
  const {
    formState: {
      payload: { rules },
    },
    handleRuleChange,
  } = useVoucherFormContext();

  // Find the single rule for this relation type
  const ruleType =
    relationType === 'allowed_countries' || relationType === 'allowed_cities'
      ? 'allowed_relations'
      : 'forbidden_relations';

  const existingRule = rules.find(r => r.type === ruleType) as
    | AllowedRelationsRule
    | ForbiddenRelationsRule
    | undefined;

  // Get the specific relation pair by ID
  const currentRelation = existingRule?.relations.find(r => r.id === relationId);
  const fromCode = currentRelation?.from || '';
  const toCode = currentRelation?.to || '';

  const [dataFrom, setDataFrom] = useState<CountriesAutocomplete>([]);
  const [dataTo, setDataTo] = useState<CountriesAutocomplete>([]);
  const [highlightFrom, setHighlightFrom] = useState('');
  const [highlightTo, setHighlightTo] = useState('');

  const getValueFromCode = (code: string): string => {
    if (!code) return '';
    const option = CountriesOptions.find(option => option.subtitle === code);
    return option?.title || '';
  };

  // Helper to update rule
  const updateRule = (newFrom: string, newTo: string) => {
    const level = relationType.includes('countries') ? 'COUNTRY' : 'CITY';
    const isAllowed = relationType.includes('allowed');
    const allRelations = existingRule?.relations || [];

    // Calculate updated relations
    const isEmpty = !newFrom && !newTo;
    let updatedRelations;

    if (isEmpty) {
      // Remove this relation pair
      updatedRelations = allRelations.filter(r => r.id !== relationId);

      // If no relations left, remove the entire rule
      if (updatedRelations.length === 0 && existingRule) {
        handleRuleChange(existingRule, true);
        return;
      }
    } else {
      // Update or add this relation pair
      const relationExists = allRelations.some(r => r.id === relationId);
      updatedRelations = relationExists
        ? allRelations.map(r =>
            r.id === relationId ? { id: relationId, from: newFrom, to: newTo } : r,
          )
        : [...allRelations, { id: relationId, from: newFrom, to: newTo }];
    }

    // Create and save the rule
    const rule = {
      type: isAllowed ? 'allowed_relations' : 'forbidden_relations',
      level,
      relations: updatedRelations,
    } as AllowedRelationsRule | ForbiddenRelationsRule;

    handleRuleChange(rule, false);
  };

  const filterData = (searchQuery: string, data: CountriesAutocomplete) => {
    return data.filter(
      item =>
        item?.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item?.subtitle?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  };

  const handleInputChange = (field: 'from' | 'to') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;

    // If input is cleared manually, update the rule
    if (searchValue === '') {
      updateRule(field === 'from' ? '' : fromCode, field === 'to' ? '' : toCode);
    }

    if (field === 'from') {
      setHighlightFrom(searchValue);
      setDataFrom(filterData(searchValue, CountriesOptions));
    } else {
      setHighlightTo(searchValue);
      setDataTo(filterData(searchValue, CountriesOptions));
    }
  };

  const handleSelect = (field: 'from' | 'to') => (selectedItem: AutocompleteCountry) => {
    if (selectedItem.subtitle) {
      const newCode = selectedItem.subtitle;
      updateRule(field === 'from' ? newCode : fromCode, field === 'to' ? newCode : toCode);

      if (field === 'from') {
        setHighlightFrom('');
        setDataFrom([]);
      } else {
        setHighlightTo('');
        setDataTo([]);
      }
    }
  };

  const handleDelete = (field: 'from' | 'to') => () => {
    updateRule(field === 'from' ? '' : fromCode, field === 'to' ? '' : toCode);
  };

  const handleReset = (field: 'from' | 'to') => () => {
    updateRule(field === 'from' ? '' : fromCode, field === 'to' ? '' : toCode);
  };

  const getLabels = () => {
    if (relationType.includes('countries')) {
      return { from: 'From country', to: 'To country' };
    }
    return { from: 'From city', to: 'To city' };
  };

  const labels = getLabels();

  return (
    <>
      <Autocomplete
        options={dataFrom}
        value={getValueFromCode(fromCode)}
        onDebounce={handleInputChange('from')}
        onDelete={handleDelete('from')}
        onReset={handleReset('from')}
        onSelect={handleSelect('from')}
      >
        <AutocompleteInput
          id={`autocomplete-from-${relationId}`}
          label={labels.from}
          type='search'
          onFocus={() => setDataFrom(CountriesOptions)}
        />
        <AutocompleteOptions label={labels.from} highlightQuery={highlightFrom} />
      </Autocomplete>
      <Autocomplete
        options={dataTo}
        value={getValueFromCode(toCode)}
        onDebounce={handleInputChange('to')}
        onDelete={handleDelete('to')}
        onReset={handleReset('to')}
        onSelect={handleSelect('to')}
      >
        <AutocompleteInput
          id={`autocomplete-to-${relationId}`}
          label={labels.to}
          type='search'
          onFocus={() => setDataTo(CountriesOptions)}
        />
        <AutocompleteOptions label={labels.to} highlightQuery={highlightTo} />
      </Autocomplete>
    </>
  );
};

export default RelationPairInputs;
